import { FC } from "react";

import { TicketingFormCategory } from "@simplyk/common";

import { CapacityShortfall } from "./getCapacityShortfall";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

interface CapacityTotalsDialogProps {
  open: boolean;
  shortfall: CapacityShortfall | null;
  formCategory: TicketingFormCategory;
  isSweepstakes?: boolean;
  isRegistration: boolean;
  isSubmitting: boolean;
  onConfirm: () => void;
  onAdjust: () => void;
}

export const CapacityTotalsDialog: FC<CapacityTotalsDialogProps> = ({
  open,
  shortfall,
  formCategory,
  isSweepstakes = false,
  isRegistration,
  isSubmitting,
  onConfirm,
  onAdjust,
}) => {
  const { t } = useTranslate();
  const isCustom = formCategory === TicketingFormCategory.Custom;
  const isLottery = formCategory === TicketingFormCategory.Lottery;

  const translationKeys = {
    sweepstakes: {
      title: "sweepstakes.capacityDialog.title",
      body: "sweepstakes.capacityDialog.body",
      adjust: "sweepstakes.capacityDialog.adjustCta",
    },
    custom: {
      title: "ticketing.capacityDialog.titleItem",
      body: "ticketing.capacityDialog.bodyItem",
      adjust: "ticketing.capacityDialog.adjustCtaItem",
    },
    lottery: {
      title: "ticketing.capacityDialog.title.lottery",
      body: "ticketing.capacityDialog.body.lottery",
      adjust: "ticketing.capacityDialog.adjustCta",
    },
    registration: {
      title: "ticketing.capacityDialog.title.registration",
      body: "ticketing.capacityDialog.body.registration",
      adjust: "ticketing.capacityDialog.adjustCta.registration",
    },
    default: {
      title: "ticketing.capacityDialog.title",
      body: "ticketing.capacityDialog.body",
      adjust: "ticketing.capacityDialog.adjustCta",
    },
  } as const;
  const category = isSweepstakes
    ? "sweepstakes"
    : isCustom
      ? "custom"
      : isLottery
        ? "lottery"
        : isRegistration
          ? "registration"
          : "default";
  const { title: titleKey, body: bodyKey } = translationKeys[category];
  const adjustCtaKey = translationKeys[isLottery && isRegistration ? "registration" : category].adjust;

  if (!shortfall) {
    return null;
  }

  return (
    <DashboardDialog open={open} onClose={onAdjust} vibe="warning" maxWidth="sm">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader title={t("dashboard", titleKey, { count: shortfall.overallSeats })} />,
          content: (
            <DashboardDialogContent>
              <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                {t("dashboard", bodyKey, {
                  overallSeats: shortfall.overallSeats,
                  sellableTotal: shortfall.sellableTotal,
                })}
              </DashboardTypography>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                onClick={onConfirm}
                isLoading={isSubmitting}
                disabled={isSubmitting}
                data-test="capacity-dialog-confirm"
              >
                {t("dashboard", "ticketing.capacityDialog.confirmCta")}
              </DashboardButton>
              <DashboardButton onClick={onAdjust} disabled={isSubmitting} data-test="capacity-dialog-adjust">
                {t("dashboard", adjustCtaKey)}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
